import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig, YAxisConfig} from '../types';

const CARDINAL_TICKS = [0, 90, 180, 270, 360];

const formatDegrees = (value: number): string => {
  switch (value) {
    case 0:
    case 360:
      return 'N';
    case 90:
      return 'E';
    case 180:
      return 'S';
    case 270:
      return 'W';
    default:
      return Units.getPolarAngleFromDegrees(value) ?? `${value}°`;
  }
};

export interface WindDirectionChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  dateTimeConfig?: DateTimeConfig;
}

export const windDirectionChart = (
  options: WindDirectionChartOptions,
): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    granularity,
    units,
    height = 200,
    dateTimeConfig,
  } = options;

  const series: SeriesConfig[] = [
    {
      id: 'wind-direction',
      type: 'scatter',
      dataKey: 'DD',
      name: 'Wind Direction',
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'linear',
      showMarkers: true,
      zIndex: 1,
    },
  ];

  const yAxis: YAxisConfig = {
    id: 'direction',
    position: 'left',
    domain: [0, 360],
    allowDecimals: false,
    ticks: CARDINAL_TICKS,
    tickFormat: formatDegrees,
    gridLines: true,
    gridColor: theme.colors.grid,
    gridWidth: theme.isDark ? 1 : 0.5,
    labelColor: theme.colors.text,
  };

  const customFormatters: Record<
    string,
    (value: number) => string
  > = {
    'wind-direction': (value: number) => {
      const label = Units.getPolarAngleFromDegrees(value);
      return label ? `${label} (${Math.round(value)}°)` : `${Math.round(value)}°`;
    },
  };

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({type: 'datetime', timezone, granularity, theme}),
    yAxes: [yAxis],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'DD',
      units,
      theme,
      dateTimeConfig,
      customFormatters,
    }),
    legend: createLegendConfig(false, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
